import React, { FC, useCallback, useContext, useState } from 'react';
import { Link, useMatch } from 'react-router-dom';
import classNames from 'classnames';
import Utils from '../utils';
import SimpleSelectionListDropDown from '../../shop/selectionlists/SimpleSelectionListDropDown';
import { Button, ButtonSize, ButtonTheme } from '../common/Button';
import { MATERIAL_ICON_ADD } from '../common/Button/Icon';
import { SelectionList } from '../../../models/SelectionList';
import { useValueChangeEffect } from '../hooks/useValueChangeEffect';
import { SideScroll, useSideScroll } from '../components/SideScroll';
import { CommentsContext } from '../../shop/selectionlists/CommentsProvider';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../utils/formatCurrency';
import { useCreateNewSelectionList } from '../../api/selection-list/useCreateNewSelectionList';
import { useSelectionLists } from '../../api/selection-list/useSelectionLists';
import { useSelectionListBar } from '../selection-list/SelectionListBarContext';
import { useStickyHeader } from '../header/useStickyHeader';

const SelectionListBar: FC = () => {
    const { t } = useTranslation();
    const [preventInitialAnimation, setPreventInitialAnimation] = useState(true);
    const { isCommentsBarOpen } = useContext(CommentsContext);
    const isOnSelectionListPage = useIsOnSelectionListPage();
    const showCommentsBar = isCommentsBarOpen && isOnSelectionListPage;
    const { selectionLists } = useSelectionLists();
    const { mutate: createAndGoToNewSelectionList } = useCreateNewSelectionList(true);
    const { isSticky } = useStickyHeader();

    useValueChangeEffect<boolean>({
        observedValue: showCommentsBar,
        effect: useCallback((newValue, prevValue) => setPreventInitialAnimation(!newValue && prevValue), [])
    });

    const { toggleSelectionListBar, selectionListBarExpanded } = useSelectionListBar();
    const { scrollLeftPossible, scrollRightPossible, prev, next, ...sideScrollProps } = useSideScroll();

    if (!selectionListBarExpanded) {
        const collapsedClasses = classNames('selection-bar-collapsed', {
            sticky: isSticky,
            slideInWithCommentsBar: showCommentsBar,
            slideOutWithCommentsBar: !showCommentsBar && !preventInitialAnimation
        });

        return (
            <a className={collapsedClasses} onClick={toggleSelectionListBar}>
                <i className="material-icons sl-collapsed-i">keyboard_arrow_left</i>
                <i className="material-icons sl-collapsed-i">content_paste</i>
            </a>
        );
    }

    return (
        <div className={classNames('selection-bar', isSticky && 'sticky')}>
            <a className="selection-recent" onClick={toggleSelectionListBar}>
                <i className="material-icons">keyboard_arrow_right</i>
                {t('selectionList.recentLists')}
            </a>
            <div className="selection-bar-nav">
                <div className="selection-bar-nav-inner">
                    <SideScroll className="selection-list" {...sideScrollProps}>
                        {selectionLists.map((list, index) => (
                            // we need to use the index as the key here, otherwise the scroll offset sticks at the
                            // previously active list, when a new list becomes the active list
                            // (needs to be investigated, but works for now)
                            <SelectionListBarItem key={index} list={list} active={index === 0} />
                        ))}
                    </SideScroll>
                </div>
                <div className="selection-list-nav">
                    {scrollLeftPossible && (
                        <a onClick={prev} className="prev">
                            <i className="material-icons">navigate_before</i>
                        </a>
                    )}
                    {scrollRightPossible && (
                        <a onClick={next} className="next">
                            <i className="material-icons">navigate_next</i>
                        </a>
                    )}
                </div>
            </div>
            <div className="selection-list-ops">
                <SimpleSelectionListDropDown selectionLists={selectionLists} />
                {/* TODO [i18n] css adjust button size (new List)*/}
                <Button
                    className="bt-sl-bar"
                    text={t('selectionList.newList')}
                    onClick={() => createAndGoToNewSelectionList(t('notifications.createAndGoToNewSelectionlist.name'))}
                    size={ButtonSize.SMALL}
                    theme={ButtonTheme.NEUTRAL}
                    icon={MATERIAL_ICON_ADD}
                />
            </div>
        </div>
    );
};

export default SelectionListBar;

function useIsOnSelectionListPage(): boolean {
    return !!useMatch('/orderlists/:id');
}

type SelectionListBarItemProps = {
    readonly list: SelectionList;
    readonly active: boolean;
};

const SelectionListBarItem: FC<SelectionListBarItemProps> = ({ list, active }) => {
    const { id: listId, listName, totalNetValue, currency, eaudiobooksAmount, ebooksAmount, epressAmount } = list;
    const { t } = useTranslation();
    const [bounce, setBounce] = useState(false);

    useValueChangeEffect({
        observedValue: totalNetValue,
        effect: useCallback(() => {
            if (active) {
                setBounce(true);
                const timeout = setTimeout(() => setBounce(false), 1200);
                return () => clearTimeout(timeout);
            }
        }, [active])
    });

    const totalPriceTag = t('numberFormat.numberWithCurrencySymbol', {
        val: FormatCurrency.convertIntegerToCurrencyFormat(totalNetValue),
        currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
    });

    const estimatedLengthOfOutput =
        (eaudiobooksAmount > 0 ? `${eaudiobooksAmount}`.length + 3 : 0) +
        (ebooksAmount > 0 ? `${ebooksAmount}`.length + 3 : 0) +
        (epressAmount > 0 ? `${epressAmount}`.length + 3 : 0) +
        totalPriceTag.length;

    return (
        <Link
            to={`/orderlists/${listId}`}
            title={listName}
            className={classNames(bounce && 'bounce', 'display-inline-flex', 'black')}
        >
            <i className="material-icons md-up">content_paste</i>
            <span>
                {Utils.truncate(listName, {
                    length: 37 - estimatedLengthOfOutput,
                    splitWords: true
                })}
            </span>
            {eaudiobooksAmount > 0 && (
                <span className="marker-badge display-inline-flex">
                    <i className="bolinda-icons">audiobook</i>
                    <span> {t('numberFormat.numberWithoutDecimalPoint', { val: eaudiobooksAmount })}</span>
                </span>
            )}
            {ebooksAmount > 0 && (
                <span className="marker-badge display-inline-flex">
                    <i className="bolinda-icons">book</i>
                    <span> {t('numberFormat.numberWithoutDecimalPoint', { val: ebooksAmount })}</span>
                </span>
            )}
            {epressAmount > 0 && (
                <span className="marker-badge display-inline-flex gild">
                    <i className="icon emagazine-filled emagazine-cart" />
                    <span> {t('numberFormat.numberWithoutDecimalPoint', { val: epressAmount })}</span>
                </span>
            )}
            <span className="marker-badge">{totalPriceTag}</span>
        </Link>
    );
};
